import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useBrandOffersAdminAuth } from '@/contexts/BrandOffersAdminAuthContext';
import { normalizeBrandOfferRecord } from '@/pages/BrandOffersPage/BrandOfferForm/BrandOfferForm.utils';
import { mergePartialOfferPatch } from '@/pages/BrandOffersPage/brandOfferPayload.utils';
import {
  BRAND_OFFERS_PAGE_COPY,
  DEFAULT_FILTERS,
  PAGE_SIZE,
} from '@/pages/BrandOffersPage/BrandOffersPage.constants';
import type { Filters } from '@/pages/BrandOffersPage/BrandOffersPage.types';
import {
  deleteBrandOffer,
  fetchBrandsForOffers,
  getBrandOffers,
  patchBrandOffer,
} from '@/services/slaydAPI/brandOffers';
import useBrandOfferStore from '@/stores/brandOfferStore';
import useToastStore from '@/stores/ToastStore';
import type {
  Brand,
  BrandOffer,
  OffersListPagination,
} from '@/types/BrandOffer.types';

function toApiParams(
  filters: Filters,
  page: number
): Record<string, string | number | undefined> {
  return {
    offer_type: filters.offerType || undefined,
    brand_id: filters.brand || undefined,
    is_active: filters.isActive || undefined,
    ordering: filters.ordering || undefined,
    page,
    page_size: PAGE_SIZE,
  };
}

export interface UseBrandOffersPageReturn {
  offers: BrandOffer[];
  totalCount: number;
  brands: Brand[];
  isLoading: boolean;
  error: string | null;
  selectedIds: Set<number>;
  filters: Filters;
  page: number;
  isFormOpen: boolean;
  editingOffer: BrandOffer | null;
  duplicateSource: BrandOffer | null;
  deleteConfirmId: number | null;
  isDeleting: boolean;
  isBulkActing: boolean;
  togglingId: number | null;
  totalPages: number;
  pageSize: number;
  hasPrevious: boolean;
  hasNext: boolean;
  brandMap: Map<number, string>;
  conflictingBrands: string[];
  pageActiveCount: number;
  allSelected: boolean;
  setPage: Dispatch<SetStateAction<number>>;
  setDeleteConfirmId: Dispatch<SetStateAction<number | null>>;
  setIsFormOpen: Dispatch<SetStateAction<boolean>>;
  setEditingOffer: Dispatch<SetStateAction<BrandOffer | null>>;
  toggleSelect: (id: number) => void;
  loadOffers: () => Promise<void>;
  handleFilterChange: (patch: Partial<Filters>) => void;
  handleLogout: () => void;
  handleToggleActive: (offer: BrandOffer) => Promise<void>;
  handleDelete: (id: number) => Promise<void>;
  handleBulkActivate: () => Promise<void>;
  handleBulkDeactivate: () => Promise<void>;
  handleBulkDelete: () => Promise<void>;
  handleFormSaved: () => void;
  openEdit: (offer: BrandOffer) => void;
  openNew: () => void;
  openDuplicate: (offer: BrandOffer) => void;
  closeFormDrawer: () => void;
  handleSelectAll: () => void;
}

export function useBrandOffersPage(): UseBrandOffersPageReturn {
  const { signed, logout: logoutAdmin } = useBrandOffersAdminAuth();

  const { brands, setBrands, setBrandsLoading } = useBrandOfferStore();
  const { showToast } = useToastStore();

  const [offers, setOffers] = useState<BrandOffer[]>([]);
  const [listPagination, setListPagination] = useState<OffersListPagination>(
    () => ({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: PAGE_SIZE,
      hasNext: false,
      hasPrevious: false,
    })
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState<number>(1);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [isBulkActing, setIsBulkActing] = useState<boolean>(false);

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingOffer, setEditingOffer] = useState<BrandOffer | null>(null);
  const [duplicateSource, setDuplicateSource] = useState<BrandOffer | null>(
    null
  );
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchRef = useRef(0);

  const loadOffers = useCallback(async (): Promise<void> => {
    if (!signed) return;
    const seq = ++fetchRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const params = toApiParams(filters, page);
      const data = await getBrandOffers(params);
      if (seq !== fetchRef.current) return;
      const normalized = data.offers.map((o) => normalizeBrandOfferRecord(o));
      setOffers(normalized);
      setListPagination(data.pagination);
      setSelectedIds(new Set());
    } catch {
      if (seq !== fetchRef.current) return;
      const msg = 'Could not load offers. Check your connection and try again.';
      setError(msg);
      showToast(msg);
    } finally {
      if (seq === fetchRef.current) setIsLoading(false);
    }
  }, [signed, filters, page, showToast]);

  useEffect(() => {
    void loadOffers();
  }, [loadOffers]);

  useEffect(() => {
    if (!signed || brands.length > 0) return;
    setBrandsLoading(true);
    fetchBrandsForOffers()
      .then((data) => setBrands(data))
      .catch((error) => {
        console.error('Error fetching brands for offers:', error);
        showToast(BRAND_OFFERS_PAGE_COPY.brandsLoadError);
      })
      .finally(() => setBrandsLoading(false));
  }, [signed, brands.length, setBrands, setBrandsLoading, showToast]);

  const totalCount = listPagination.totalItems;
  const totalPages = listPagination.totalPages;
  const pageSize = listPagination.itemsPerPage;
  const hasPrevious = listPagination.hasPrevious;
  const hasNext = listPagination.hasNext;

  const brandMap = useMemo(
    () => new Map(brands.map((b) => [b.id, b.name])),
    [brands]
  );

  const conflictingBrands = useMemo((): string[] => {
    const counts: Record<number, number> = {};
    offers.forEach((o) => {
      if (o.isActive && o.brand !== null) {
        counts[o.brand] = (counts[o.brand] ?? 0) + 1;
      }
    });
    return Object.entries(counts)
      .filter(([, count]) => count > 1)
      .map(([id]) => brandMap.get(Number(id)) ?? `Brand #${id}`);
  }, [offers, brandMap]);

  const pageActiveCount = useMemo(
    () => offers.filter((o) => o.isActive).length,
    [offers]
  );

  const allSelected =
    offers.length > 0 && offers.every((o) => selectedIds.has(o.id));

  const handleSelectAll = useCallback((): void => {
    setSelectedIds((prev) => {
      if (offers.every((o) => prev.has(o.id))) return new Set();
      return new Set(offers.map((o) => o.id));
    });
  }, [offers]);

  const toggleSelect = useCallback((id: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleFilterChange = useCallback((patch: Partial<Filters>): void => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const handleLogout = useCallback((): void => {
    logoutAdmin();
    window.location.href = '/login';
  }, [logoutAdmin]);

  const handleToggleActive = useCallback(
    async (offer: BrandOffer): Promise<void> => {
      setTogglingId(offer.id);
      try {
        await patchBrandOffer(
          offer.id,
          mergePartialOfferPatch(offer, {
            offerType: offer.offerType,
            brand: offer.brand,
            priority: offer.priority,
            isActive: !offer.isActive,
          })
        );
        await loadOffers();
        showToast(
          offer.isActive ? 'Offer paused.' : 'Offer is live on the app.'
        );
      } catch {
        const msg = 'Toggle failed. Please try again.';
        setError(msg);
        showToast(msg);
      } finally {
        setTogglingId(null);
      }
    },
    [loadOffers, showToast]
  );

  const handleDelete = useCallback(
    async (id: number): Promise<void> => {
      setIsDeleting(true);
      try {
        await deleteBrandOffer(id);
        setDeleteConfirmId(null);
        await loadOffers();
      } catch {
        const msg = 'Delete failed. The offer may still be referenced.';
        setError(msg);
        showToast(msg);
      } finally {
        setIsDeleting(false);
      }
    },
    [loadOffers, showToast]
  );

  const setBulkActive = useCallback(
    async (nextActive: boolean): Promise<void> => {
      const targets = offers.filter((o) => selectedIds.has(o.id));
      await Promise.all(
        targets.map((o) =>
          patchBrandOffer(
            o.id,
            mergePartialOfferPatch(o, {
              offerType: o.offerType,
              brand: o.brand,
              priority: o.priority,
              isActive: nextActive,
            })
          )
        )
      );
    },
    [offers, selectedIds]
  );

  const handleBulkActivate = useCallback(async (): Promise<void> => {
    setIsBulkActing(true);
    try {
      await setBulkActive(true);
      setSelectedIds(new Set());
      await loadOffers();
    } catch {
      const msg = 'Bulk activate failed. Refresh and try again.';
      setError(msg);
      showToast(msg);
    } finally {
      setIsBulkActing(false);
    }
  }, [setBulkActive, loadOffers, showToast]);

  const handleBulkDeactivate = useCallback(async (): Promise<void> => {
    setIsBulkActing(true);
    try {
      await setBulkActive(false);
      setSelectedIds(new Set());
      await loadOffers();
    } catch {
      const msg = 'Bulk deactivate failed. Refresh and try again.';
      setError(msg);
      showToast(msg);
    } finally {
      setIsBulkActing(false);
    }
  }, [setBulkActive, loadOffers, showToast]);

  const handleBulkDelete = useCallback(async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setIsBulkActing(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => deleteBrandOffer(id))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const total = results.length;
      const succeeded = total - failed;

      if (failed === total) {
        const msg = 'Bulk delete failed. Some items may still exist.';
        setError(msg);
        showToast(msg);
      } else if (failed > 0) {
        const msg = `Deleted ${succeeded} of ${total} offers. ${failed} could not be deleted.`;
        setError(msg);
        showToast(msg);
      } else {
        setSelectedIds(new Set());
      }
    } finally {
      await loadOffers();
      setIsBulkActing(false);
    }
  }, [selectedIds, loadOffers, showToast]);

  const openNew = useCallback((): void => {
    setEditingOffer(null);
    setDuplicateSource(null);
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((offer: BrandOffer): void => {
    setDuplicateSource(null);
    setEditingOffer(offer);
    setIsFormOpen(true);
  }, []);

  const openDuplicate = useCallback((offer: BrandOffer): void => {
    setEditingOffer(null);
    setDuplicateSource(offer);
    setIsFormOpen(true);
  }, []);

  const closeFormDrawer = useCallback((): void => {
    setIsFormOpen(false);
    setEditingOffer(null);
    setDuplicateSource(null);
  }, []);

  const handleFormSaved = useCallback((): void => {
    closeFormDrawer();
    void loadOffers();
  }, [closeFormDrawer, loadOffers]);

  return {
    offers,
    totalCount,
    brands,
    isLoading,
    error,
    selectedIds,
    filters,
    page,
    isFormOpen,
    editingOffer,
    duplicateSource,
    deleteConfirmId,
    isDeleting,
    isBulkActing,
    togglingId,
    totalPages,
    pageSize,
    hasPrevious,
    hasNext,
    brandMap,
    conflictingBrands,
    pageActiveCount,
    allSelected,
    setPage,
    setDeleteConfirmId,
    setIsFormOpen,
    setEditingOffer,
    toggleSelect,
    loadOffers,
    handleFilterChange,
    handleLogout,
    handleToggleActive,
    handleDelete,
    handleBulkActivate,
    handleBulkDeactivate,
    handleBulkDelete,
    handleFormSaved,
    openEdit,
    openNew,
    openDuplicate,
    closeFormDrawer,
    handleSelectAll,
  };
}
